'use client';

/**
 * Simplified footer: left = Impressum/Privacy; center = commit id of current route's package;
 * right = package name and version (e.g. @korvo/editor v0.1.2).
 * Fetches /api/footer/git-info?package=<name> based on pathname (e.g. /projects → workspace package).
 */

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { getPackageForPathname } from '@/lib/footer-package-for-path';

type GitInfoPayload = {
  name?: string;
  version?: string;
  commit?: string;
  commitTimestamp?: string;
  buildId?: string;
};

export function AppFooter() {
  const pathname = usePathname();
  const packageName = getPackageForPathname(pathname ?? '/');
  const [info, setInfo] = useState<GitInfoPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/footer/git-info?package=${encodeURIComponent(packageName)}`);
        if (!res.ok) {
          setError(null);
          setInfo(null);
          return;
        }
        const data = (await res.json()) as GitInfoPayload;
        if (!cancelled) {
          setInfo(data);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Footer error');
          setInfo(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [packageName]);

  const commit = info?.commit ?? '—';
  const buildId = info?.buildId != null ? info.buildId : '';
  const commitDisplay = info ? (buildId ? `${commit} · ${buildId}` : commit) : '—';
  const centerRight = error ? error : info ? commitDisplay : 'Loading…';

  const displayName = info?.name === 'app' || !info?.name ? 'root' : `@korvo/${info.name}`;
  const version = info?.version ?? '—';
  const rightText = info ? `Project Korvo · ${displayName} version ${version}` : 'Project Korvo · version —';

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-10 safe-area-y"
      style={{
        height: 'calc(var(--footer-height, 2rem) + var(--safe-area-inset-bottom, 0px))',
        minHeight: 'calc(var(--footer-height, 2rem) + var(--safe-area-inset-bottom, 0px))',
        backgroundColor: 'var(--footer-background)',
      }}
    >
      <footer
        className="w-full h-full"
        style={{
          backgroundColor: 'var(--footer-background)',
          color: 'var(--footer-foreground)',
        }}
      >
        <div
          className="px-2 sm:px-3 h-full flex items-center justify-between gap-2 sm:gap-4 text-xs sm:text-sm"
          style={{ color: 'var(--footer-foreground-muted)' }}
        >
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link
              href="/impressum"
              className="hover:underline transition-colors"
              style={{ color: 'var(--footer-foreground-muted)' }}
            >
              Impressum
            </Link>
            <Link
              href="/privacy"
              className="hover:underline transition-colors"
              style={{ color: 'var(--footer-foreground-muted)' }}
            >
              Privacy Policy
            </Link>
          </div>

          <div className="flex-1 mx-1 sm:mx-2 overflow-hidden">
            <div
              className="text-center whitespace-nowrap overflow-hidden text-ellipsis font-mono"
              style={{ color: 'var(--footer-foreground-muted)' }}
              title={`Alpha · ${centerRight}`}
            >
              <span style={{ color: 'var(--footer-foreground)' }} aria-label="Product stage">Alpha</span>
              <span style={{ color: 'var(--footer-foreground-muted)' }} aria-hidden> · </span>
              {centerRight}
            </div>
          </div>

          <div
            className="shrink-0 text-[0.7rem] sm:text-xs text-right font-mono"
            style={{ color: 'var(--footer-foreground-muted)' }}
          >
            {rightText}
          </div>
        </div>
      </footer>
    </div>
  );
}
